/**
 * HOME PAGE
 * 
 * This is the landing page users see when they visit the app.
 * It shows a welcome message and navigation to main features.
 * Glassmorphic styling
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  // Listen for auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
    });
    return () => unsubscribe();
  }, []);

  // Handle Get Started click
  const handleGetStarted = () => {
    if (isLoggedIn) {
      navigate('/collection');
    } else {
      setShowSignupPrompt(true);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4">
      {/* Glassmorphic card container */}
      <div className="glass-card p-8">

        <h1
          className="text-4xl mb-4 text-glass font-heading"
        >
          Welcome to Your Nail Art Collection! 💅
        </h1>

        <p
          className="text-glass-secondary text-lg mb-8 font-body"
        >
          Organize your nail polish collection, create amazing nail art projects,
          and connect with other nail art enthusiasts!
        </p>
        
        {/* Feature cards */}
        <div className="space-y-6 mb-8">
          <div className="glass-feature-card border-l-rainbow-fuchsia">
            <h3
              className="text-xl font-bold text-glass mb-2 font-body"
            >
              📚 Organize Your Collection
            </h3>
            <p
              className="text-glass-secondary font-body"
            >
              Keep track of all your polishes, stamping plates, tools, and embellishments.
              Search by brand, color, or finish type.
            </p>
          </div>

          <div className="glass-feature-card border-l-rainbow-yellow">
            <h3
              className="text-xl font-bold text-glass mb-2 font-body"
            >
              🎨 Create Projects
            </h3>
            <p
              className="text-glass-secondary font-body"
            >
              Document your nail art creations with photos and track which
              products you used - like a recipe for your nails!
            </p>
          </div>

          <div className="glass-feature-card border-l-rainbow-green">
            <h3
              className="text-xl font-bold text-glass mb-2 font-body"
            >
              🛒 Buy & Sell
            </h3>
            <p
              className="text-glass-secondary font-body"
            >
              List polishes you want to destash and browse what others are selling.
              Connect with collectors around the world!
            </p>
          </div>
        </div>
        
        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleGetStarted}
            className="btn-primary px-8 font-body"
          >
            Get Started
          </button>
          <Link
            to="/marketplace"
            className="btn-secondary px-8 font-body"
          >
            Browse Marketplace
          </Link>
        </div>
        
      </div>

      {/* Signup Prompt Modal */}
      {showSignupPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-md w-full p-8">
            <h2
              className="text-2xl font-bold text-glass mb-4 font-heading"
            >
              ✨ Join Polish ALL the Things!
            </h2>

            <p
              className="text-glass-secondary mb-6 font-body"
            >
              Create a free account to unlock all features:
            </p>

            <ul className="space-y-3 mb-6 font-body">
              <li className="flex items-center gap-3 text-glass-secondary">
                <span className="text-glass">✓</span>
                <span>Organize your entire collection</span>
              </li>
              <li className="flex items-center gap-3 text-glass-secondary">
                <span className="text-glass">✓</span>
                <span>Track favorites, wishlists & destash items</span>
              </li>
              <li className="flex items-center gap-3 text-glass-secondary">
                <span className="text-glass">✓</span>
                <span>Create nail art project galleries</span>
              </li>
              <li className="flex items-center gap-3 text-glass-secondary">
                <span className="text-glass">✓</span>
                <span>Share your collection with friends</span>
              </li>
              <li className="flex items-center gap-3 text-glass-secondary">
                <span className="text-glass">✓</span>
                <span>Buy & sell in the marketplace</span>
              </li>
            </ul>

            <div className="flex flex-col gap-3">
              <Link
                to="/auth"
                onClick={() => setShowSignupPrompt(false)}
                className="btn-primary w-full text-center font-body"
              >
                Sign Up / Login
              </Link>
              <button
                onClick={() => setShowSignupPrompt(false)}
                className="w-full py-3 text-glass-muted hover:text-glass transition-default font-body"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
